import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { NotePencil, FilePdf, Trash, MagnifyingGlass, Highlighter as HighlighterIcon } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EmptyState from "@/components/EmptyState";
import { HL_COLORS } from "@/components/Highlighter";

const dotOf = (id) => HL_COLORS.find((c) => c.id === id)?.dot || "#FACC15";

export default function MesNotes() {
  const [groups, setGroups] = useState(null);
  const [color, setColor] = useState("all");
  const [q, setQ] = useState("");

  const load = () => api.get("/my-notes").then((r) => setGroups(r.data.groups)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/highlights/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const exportPdf = async () => {
    try {
      const res = await api.get("/my-notes/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "mes-notes.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  // filtre par couleur et recherche texte ; regroupement par cours
  const byCourse = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = {};
    (groups || []).forEach((g) => {
      const hls = g.highlights.filter((h) =>
        (color === "all" || h.color === color) &&
        (!needle || h.text.toLowerCase().includes(needle) || (h.note || "").toLowerCase().includes(needle)));
      const noteMatch = color === "all" && g.note && (!needle || g.note.toLowerCase().includes(needle));
      if (!hls.length && !noteMatch) return;
      (out[g.course_id] = out[g.course_id] || { title: g.course_title, items: [] }).items.push({ ...g, highlights: hls, note: noteMatch ? g.note : "" });
    });
    return Object.entries(out);
  }, [groups, color, q]);

  const total = (groups || []).reduce((n, g) => n + g.highlights.length, 0);

  return (
    <div className="space-y-6" data-testid="mes-notes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Révisions</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Mes notes</h1>
          <p className="text-sm text-muted-foreground">Vos passages surlignés et vos notes, classés par cours. Visibles par vous seul.</p>
        </div>
        <Button variant="outline" onClick={exportPdf} disabled={!groups || groups.length === 0} data-testid="notes-pdf">
          <FilePdf size={16} className="mr-2" /> Exporter en PDF
        </Button>
      </div>

      {groups && groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
          <div className="relative min-w-[200px] flex-1">
            <MagnifyingGlass size={16} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher dans mes notes..." className="pl-8" data-testid="notes-search" />
          </div>
          <button type="button" onClick={() => setColor("all")}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${color === "all" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
            Tout ({total})
          </button>
          {HL_COLORS.map((c) => (
            <button key={c.id} type="button" onClick={() => setColor(c.id)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${color === c.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
              <span className="h-3 w-3 rounded-full" style={{ background: c.dot }} /> {c.label}
            </button>
          ))}
        </div>
      )}

      {groups === null ? (
        <p className="text-sm text-muted-foreground">Chargement...</p>
      ) : groups.length === 0 ? (
        <EmptyState icon={HighlighterIcon} title="Aucune note pour le moment"
          description="Pendant la lecture d'une séquence, sélectionnez un passage pour le surligner ou y ajouter une note. Tout apparaîtra ici." testId="notes-empty" />
      ) : byCourse.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun résultat pour ce filtre.</p>
      ) : (
        byCourse.map(([courseId, course]) => (
          <section key={courseId} className="rounded-xl border border-border bg-card p-5" data-testid={`notes-course-${courseId}`}>
            <h2 className="font-display text-lg font-bold text-primary">{course.title}</h2>
            <div className="mt-3 space-y-5">
              {course.items.map((g) => (
                <div key={g.lesson_id}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.section_title ? `${g.section_title} · ` : ""}{g.lesson_title}
                  </p>
                  {g.note && (
                    <div className="mt-2 rounded-lg bg-secondary p-3 text-sm">
                      <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-muted-foreground"><NotePencil size={13} /> Ma note</p>
                      <p className="whitespace-pre-wrap">{g.note}</p>
                    </div>
                  )}
                  <ul className="mt-2 space-y-2">
                    {g.highlights.map((h) => (
                      <li key={h.id} className="flex items-start gap-3 rounded-lg border border-border p-3" style={{ borderLeft: `4px solid ${dotOf(h.color)}` }}>
                        <div className="min-w-0 flex-1">
                          <Link to={`/app/cours/${g.course_id}?sequence=${g.lesson_id}&hl=${h.id}`} className="text-sm leading-relaxed hover:underline" data-testid="note-open">
                            « {h.text} »
                          </Link>
                          {h.note && <p className="mt-1.5 flex gap-1 text-sm text-muted-foreground"><NotePencil size={14} className="mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{h.note}</span></p>}
                        </div>
                        <button type="button" onClick={() => remove(h.id)} className="text-muted-foreground hover:text-accent" aria-label="Supprimer" data-testid="note-delete"><Trash size={15} /></button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
